import React, { useEffect, useRef, useState } from "react";
import "./CameraScreen.css";
import GallerySelect from "./UI/GallerySelect";
import CameraCapture from "./UI/CameraCapture";
import FilterButton from "./UI/FilterButton";
import FilterSection from "./UI/FilterSection";
import useImageUpload from "../../hooks/useImageUpload";
import CameraOptions from "./UI/CameraOptions";
import TopActions from "./UI/TopActions";
import ShotsIndicator from "./UI/ShotsIndicator";
import UploadProgressOverlay from "./UI/UploadProgressOverlay";
import useCombinedImageUpload from "../../hooks/useCombinedImageUpload";
import { useNavigate } from "react-router-dom";
import CameraWebcam from "./UI/CameraWebcam";
import useWebcamHook from "../../hooks/qr/useWebcamHook";
import useApplyFiltersHook from "../../hooks/qr/useApplyFiltersHook";
import ThumbnailSlider from "./UI/ThumbnailSlider";
import usePreviewImagesHook from "../../hooks/qr/usePreviewImagesHook";
/* ---------------- FILTERS ---------------- */

const CameraErrorContainer = ({ error, description, onClick, btnText, children }) => (
  <div className="camera-error">
    <div className="camera-error-card">
      {error && <h2 className="camera-error-title">{error}</h2>}

      {description && <p className="camera-error-text">{description}</p>}
      {children}

      {onClick && (
        <button className="camera-error-btn" onClick={onClick}>
          {btnText}
        </button>
      )}
    </div>
  </div>
);

export default function CameraScreen({
  totalUploads,
  uploadedCount,
  canUpload,
  fetchGuestImagesCount,
  event_id,
}) {
  // Disable page zooming when camera screen is active
  useEffect(() => {
    // Prevent pinch-to-zoom on mobile
    const preventZoom = (e) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    };

    // Prevent double-tap zoom
    const preventDoubleTapZoom = (e) => {
      if (e.touches.length === 1) {
        e.preventDefault();
      }
    };

    // Add event listeners
    document.addEventListener('touchstart', preventZoom, { passive: false });
    document.addEventListener('touchmove', preventZoom, { passive: false });
    document.addEventListener('touchend', preventDoubleTapZoom, { passive: false });

    // Disable zoom via meta tag
    const originalViewport = document.querySelector('meta[name="viewport"]')?.getAttribute('content');
    const metaViewport = document.querySelector('meta[name="viewport"]') || document.createElement('meta');
    metaViewport.name = 'viewport';
    metaViewport.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover';
    
    if (!document.querySelector('meta[name="viewport"]')) {
      document.head.appendChild(metaViewport);
    }

    // Cleanup function
    return () => {
      document.removeEventListener('touchstart', preventZoom);
      document.removeEventListener('touchmove', preventZoom);
      document.removeEventListener('touchend', preventDoubleTapZoom);
      
      // Restore original viewport
      if (originalViewport) {
        document.querySelector('meta[name="viewport"]').setAttribute('content', originalViewport);
      }
    };
  }, []); // Run only on mount and unmount
  // Webcam hook - manages camera controls and settings
  const {
    webcamRef, // Ref for webcam element
    flipCamera, // Function to switch between front/back camera
    facingMode, // Current camera direction ('user' or 'environment')
    flashMode, // Current flash setting ('off', 'on', 'auto')
    setFlashMode, // Function to change flash mode
    ratio, // Current aspect ratio setting
    setRatio, // Function to change aspect ratio
    cameraError, // Camera error message
    handleCameraError, // Function to handle camera errors
    timer, // Timer duration for delayed capture (0=immediate, 3=3sec, 10=10sec)
    setTimer, // Function to set timer duration
    countdown, // Current countdown value during timer (null when not counting)
    setCountdown, // Function to update countdown display
  } = useWebcamHook();

  // Filter hook - manages camera filter effects
  const { activeFilter, setActiveFilter, filterMap } = useApplyFiltersHook();

  const {
    previewRef,
    canvasRef,
    selectedImages,
    setSelectedImages,
    addImage,
    activeImageId,
    setActiveImageId,
  } = usePreviewImagesHook();
  const optionsRef = useRef(null);
  const bottomPanelRef = useRef(null);
  const controlsRef = useRef(null);
  const shotsIndicatorRef = useRef(null);
  const navigate = useNavigate();
  /**
   * {
   *   id,
   *   original,
   *   filtered,
   *   filter,
   *   status,   // pending | uploading | success | error
   * }
   */
  const [showFilters, setShowFilters] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [filterPanelBottom, setFilterPanelBottom] = useState(150); // Default fallback value
  const [controlsHeight, setControlsHeight] = useState(0);

  const {
    uploadMultipleImages,
    loading: uploading,
    error,
    progress,
  } = useCombinedImageUpload();
  useEffect(() => {
    if (uploading) setShowFilters(false);
  }, [uploading]);

  useEffect(() => {
    if (!showOptions) return;

    const handleClickOutside = (e) => {
      if (optionsRef.current && !optionsRef.current.contains(e.target)) {
        setShowOptions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showOptions]);

  // Calculate bottom panel height for filter positioning
  useEffect(() => {
    const updateFilterPosition = () => {
      if (bottomPanelRef.current && shotsIndicatorRef.current) {
        const bottomPanelHeight = bottomPanelRef.current.offsetHeight;
        const shotsIndicatorHeight = shotsIndicatorRef.current.offsetHeight;
        setFilterPanelBottom(bottomPanelHeight + shotsIndicatorHeight + 16);
      }
    };

    updateFilterPosition();

    // Update on window resize and when filters visibility changes
    window.addEventListener("resize", updateFilterPosition);

    return () => {
      window.removeEventListener("resize", updateFilterPosition);
    };
  }, [showFilters]);

  // Calculate controls height for ShotsIndicator positioning
  useEffect(() => {
    const updateControlsHeight = () => {
      if (controlsRef.current) {
        const height = controlsRef.current.offsetHeight;
        setControlsHeight(height);
      }
    };

    updateControlsHeight();

    // Update on window resize
    window.addEventListener("resize", updateControlsHeight);

    return () => {
      window.removeEventListener("resize", updateControlsHeight);
    };
  }, []);

  /* ---------------- UTILS ---------------- */
  const dataURLtoFile = (dataUrl, fileName) => {
    const arr = dataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], fileName, { type: mime });
  };

  const uploadAllImages = async () => {
    // Filter out already successful images
    const imagesToUpload = selectedImages.filter(
      (img) => img.status !== "success",
    );

    if (imagesToUpload.length === 0) return;

    // Mark all as uploading
    setSelectedImages((prev) =>
      prev.map((i) =>
        imagesToUpload.some((img) => img.id === i.id)
          ? { ...i, status: "uploading" }
          : i,
      ),
    );

    // Convert data URLs to files
    const files = imagesToUpload.map((img) =>
      dataURLtoFile(img.filtered, `photo-${Date.now()}-${img.id}.png`),
    );

    // Use batch upload
    const results = await uploadMultipleImages({ images: files, event_id });

    if (results) {
      // Clear local images and refresh count
      setSelectedImages([]);
      fetchGuestImagesCount();
      setShowFilters(false);
    } else {
      // Mark all as error
      setSelectedImages((prev) =>
        prev.map((i) =>
          imagesToUpload.some((img) => img.id === i.id)
            ? { ...i, status: "error" }
            : i,
        ),
      );
    }
  };

  const activeImage = selectedImages.find((img) => img.id === activeImageId);

  /* ---------------- UI ---------------- */
  return (
    <div className="camera-page">
      <div className="camera-container">
        <canvas ref={canvasRef} hidden />
        {/* Top Actions */}
        <TopActions
          hasSelection={selectedImages.length > 0}
          cameraError={cameraError}
          setShowOptions={setShowOptions}
          flipCamera={flipCamera}
          onUploadImage={uploadAllImages}
          uploadedCount={uploadedCount}
          canUpload={canUpload}
          disabled={!selectedImages.length}
          onRemoveActive={() => {
            setSelectedImages([]);
            setActiveImageId(null);
          }}
          event_id={event_id}
        />

        {/* 
        Camera options panel (dropdown)
        - Shows when showOptions state is true
        - Contains flash, timer, and ratio settings
        - Positioned absolutely with ref for click-outside detection
      */}
        {showOptions && (
          <div ref={optionsRef}>
            <CameraOptions
              flashMode={flashMode}
              setFlashMode={setFlashMode}
              timer={timer}
              setTimer={setTimer}
              ratio={ratio}
              setRatio={setRatio}
            />
          </div>
        )}

        {/* PREVIEW */}
        <div
          style={{
            position: "relative",
            display: "flex",
            width: "100%",
            height: "100%",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div className="camera-preview" ref={previewRef}>
            {selectedImages?.length ? (
              <div className="preview-strip">
                <ThumbnailSlider
                  selectedImages={selectedImages}
                  activeImageId={activeImageId}
                  setActiveImageId={setActiveImageId}
                  setSelectedImages={setSelectedImages}
                />
              </div>
            ) : (
              <CameraWebcam
                webcamRef={webcamRef}
                facingMode={facingMode}
                flashMode={flashMode}
                ratio={ratio}
                handleCameraError={handleCameraError}
                filter={filterMap[activeFilter]}
                controlsHeight={controlsHeight}
              />
            )}

            {/* COUNTDOWN OVERLAY */}
            {countdown !== null && (
              <div className="countdown-overlay">
                <span>{countdown}</span>
              </div>
            )}

            {/* Upload limit reached message */}
            {!canUpload && uploadedCount >= totalUploads ? (
              <CameraErrorContainer
                error={"You’ve hit the 5-pic limit!."}
                description={`Your free plan includes 5 uploads.
        Wanna add more? Delete one or upload your images`}
                onClick={() =>
                  navigate("/user-uploaded-images?eventId=" + event_id)
                }
                btnText={"Manage Album"}
              />
            ) : (
              // Show camera error only when no image is selected and camera has an error
              !selectedImages.length &&
              cameraError && (
                <CameraErrorContainer description={cameraError}>
                  <p className="camera-error-text">You can still select photos from your gallery.</p>
                  <GallerySelect
                    buttonLabel="Select from gallery"
                    addImage={(image) => image ? addImage(image) : setSelectedImages([])}
                    remainingUploads={totalUploads - uploadedCount}
                    totalUploads={totalUploads}
                  />
                </CameraErrorContainer>
              )
            )}
          </div>
        </div>

        {/* FILTERS */}
        {showFilters && !uploading && (
          <FilterSection
            filterMap={filterMap}
            canvasRef={canvasRef}
            setActiveFilter={setActiveFilter}
            activeFilter={activeFilter}
            activeImage={activeImage}
            activeImageId={activeImageId}
            setSelectedImages={setSelectedImages}
            filterPanelBottom={filterPanelBottom}
            selectedImages={selectedImages}
          />
        )}

        {/* CONTROLS */}
        {!canUpload && uploadedCount >= totalUploads ? null : (
          <div className="bottom-panel" ref={bottomPanelRef}>
            {uploadedCount < totalUploads && (
              <ShotsIndicator
                refer={shotsIndicatorRef}
                total={totalUploads}
                used={uploadedCount}
                controlsHeight={controlsHeight}
              />
            )}

            <div className="controls" ref={controlsRef}>
              {/* Gallery button - opens file picker for image selection */}
              <GallerySelect
                addImage={(val) =>
                  val ? addImage(val) : setSelectedImages([])
                }
                remainingUploads={totalUploads - uploadedCount}
                totalUploads={totalUploads}
              />

              {/* Camera capture button - handles photo capture with timer and filters */}
              <CameraCapture
                webcamRef={webcamRef}
                previewRef={previewRef}
                setCameraError={handleCameraError}
                cameraError={cameraError}
                canvasRef={canvasRef}
                filterMap={filterMap}
                activeFilter={activeFilter}
                addImage={addImage}
                timer={timer}
                selectedImages={selectedImages}
                setCountdown={setCountdown}
                countdown={countdown}
                ratio={ratio}
              />

              {/* Filters toggle button - shows/hides filter options */}
              <FilterButton
                onToggle={() => setShowFilters((prev) => !prev)}
                selectedImages={selectedImages}
                cameraError={cameraError}
              />
            </div>
            {uploading && <UploadProgressOverlay progress={progress} />}
          </div>
        )}
      </div>
    </div>
  );
}
